import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConsentStatusRichBadge, CallStatusIcon } from "@/components/ui/status-badge";
import { useConsentsStore } from "@/stores/consentsStore";
import { usePatientsStore } from "@/stores/patientsStore";
import { PatientResponse } from "@/lib/types";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatDateOnly, formatTimestamp, timestampMs } from "@/lib/datetime";

interface PatientTableProps {
  patients: PatientResponse[];
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

const PatientTable = ({
  patients,
  currentPage,
  pageSize,
  totalPages,
}: PatientTableProps) => {
  const navigate = useNavigate();
  const { getLatestConsentForPatient } = useConsentsStore();
  const { togglePatientSelection, isPatientSelected } = usePatientsStore();

  const columns = useMemo<ColumnDef<PatientResponse>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              patients.length > 0 &&
              patients.every((patient) => isPatientSelected(patient.id))
            }
            onCheckedChange={(value) => {
              patients.forEach((patient) => {
                // If value is true, select all patients that aren't already selected
                // If value is false, deselect all patients
                if (!!value !== isPatientSelected(patient.id)) {
                  togglePatientSelection(patient);
                }
              });
            }}
            aria-label="Select all"
            className="h-5 w-5"
          />
        ),
        cell: ({ row }) => {
          const patient = row.original;
          return (
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex items-center justify-start py-3 pl-4 cursor-default"
            >
              <Checkbox
                checked={isPatientSelected(patient.id)}
                onCheckedChange={() => togglePatientSelection(patient)}
                aria-label="Select row"
                className="h-5 w-5"
              />
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "name",
        accessorFn: (row) => `${row.first_name} ${row.last_name}`,
        header: "Name",
        cell: ({ row }) => {
          const patient = row.original;
          return (
            <div className="text-[13px] font-medium">
              {patient.first_name} {patient.last_name}
              {patient.preferred_name && (
                <span className="text-muted-foreground font-normal ml-1.5 text-xs">
                  ({patient.preferred_name})
                </span>
              )}
              {patient.external_id && (
                <div
                  className="text-[11px] font-normal text-muted-foreground tabular-nums"
                  title="MRN / external id"
                >
                  MRN {patient.external_id}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "date_of_birth",
        header: "DOB",
        cell: ({ row }) => {
          const dob = row.original.date_of_birth;
          if (!dob) return <div className="text-xs text-muted-foreground">—</div>;

          // A calendar date: formatted without any time-zone shift.
          return (
            <div className="text-xs text-muted-foreground tabular-nums">{formatDateOnly(dob, "MM/dd/yyyy", dob)}</div>
          );
        },
      },
      {
        id: "consent",
        header: "Consent",
        enableSorting: false,
        cell: ({ row }) => {
          const patient = row.original;

          // Use v3 consent status if available, otherwise use the store lookup
          if (patient.latest_consent_status) {
            return (
              <ConsentStatusRichBadge
                status={patient.latest_consent_status}
                method={patient.consent_method}
                needsAction={patient.needs_consent}
              />
            );
          }

          const latestConsent = getLatestConsentForPatient(patient.id);
          return (
            <ConsentStatusRichBadge
              status={latestConsent?.consent_status}
            />
          );
        },
      },
      {
        id: "call_status",
        header: () => <div className="text-center">Latest Call</div>,
        enableSorting: false,
        cell: ({ row }) => {
          const patient = row.original;

          // Handle both v2 (with calls array) and v3 (with flattened call data) formats
          if (patient.calls && patient.calls.length > 0) {
            const sortedCalls = [...patient.calls].sort(
              (a, b) =>
                timestampMs(b.created_at) - timestampMs(a.created_at)
            );
            const latestCall = sortedCalls[0];

            return (
              <div className="flex justify-center">
              <Popover>
                <PopoverTrigger asChild>
                  <span className="cursor-help">
                    <CallStatusIcon status={latestCall.call_status} />
                  </span>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3">
                  <div className="space-y-2">
                    <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">Call History</h4>
                    <div className="space-y-1.5">
                      {sortedCalls.slice(0, 5).map((call) => (
                        <div key={call.id} className="flex items-center justify-between text-xs">
                          <CallStatusIcon status={call.call_status} />
                          <span className="text-muted-foreground">
                            {formatTimestamp(call.created_at, "MMM d, h:mm a")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              </div>
            );
          } else if (patient.latest_call_status) {
            return (
              <div className="flex justify-center">
              <Popover>
                <PopoverTrigger asChild>
                  <span className="cursor-help">
                    <CallStatusIcon status={patient.latest_call_status} />
                  </span>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3">
                  <div className="space-y-2 text-xs">
                    <h4 className="font-medium text-muted-foreground uppercase tracking-wider">Call Info</h4>
                    <div className="space-y-1">
                      {patient.total_calls && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total</span>
                          <span className="font-medium">{patient.total_calls}</span>
                        </div>
                      )}
                      {patient.success_rate !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Success</span>
                          <span className="font-medium">{(patient.success_rate * 100).toFixed(0)}%</span>
                        </div>
                      )}
                      {patient.last_call_date && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Last call</span>
                          <span>{formatTimestamp(patient.last_call_date, "MMM d, h:mm a")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              </div>
            );
          } else {
            return (
              <div className="flex justify-center">
                <span className="text-xs text-muted-foreground">—</span>
              </div>
            );
          }
        },
      },
      {
        id: "tags",
        header: "Tags",
        enableSorting: false,
        cell: ({ row }) => {
          const patient = row.original;
          const tags = patient.tags || [];

          if (tags.length === 0) {
            return (
              <span className="text-xs text-muted-foreground">—</span>
            );
          }

          return (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 3).map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="text-[11px] font-normal px-1.5 py-0"
                >
                  {tag}
                </Badge>
              ))}
              {tags.length > 3 && (
                <span className="text-[11px] text-muted-foreground">+{tags.length - 3}</span>
              )}
            </div>
          );
        },
      },
    ],
    [togglePatientSelection, isPatientSelected, patients]
  );

  const table = useReactTable({
    data: patients,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages > 0 ? totalPages : -1,
  });

  // Phone card list helpers (the <Table> below is desktop-only).
  const latestCallStatus = (patient: PatientResponse) => {
    if (patient.calls && patient.calls.length > 0) {
      const sorted = [...patient.calls].sort(
        (a, b) =>
          timestampMs(b.created_at) - timestampMs(a.created_at)
      );
      return sorted[0].call_status;
    }
    return patient.latest_call_status ?? null;
  };

  const formatDob = (dob?: string | null) => {
    if (!dob) return null;
    return formatDateOnly(dob, "MM/dd/yyyy", dob);
  };

  const renderConsent = (patient: PatientResponse) => {
    if (patient.latest_consent_status) {
      return (
        <ConsentStatusRichBadge
          status={patient.latest_consent_status}
          method={patient.consent_method}
          needsAction={patient.needs_consent}
        />
      );
    }
    const latestConsent = getLatestConsentForPatient(patient.id);
    return <ConsentStatusRichBadge status={latestConsent?.consent_status} />;
  };

  const allSelected =
    patients.length > 0 &&
    patients.every((patient) => isPatientSelected(patient.id));

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card">
      {/* Phone: card list */}
      <div className="md:hidden flex-1 overflow-auto min-h-0">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-muted/80 px-3 py-2 backdrop-blur-sm">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(value) => {
              patients.forEach((patient) => {
                if (!!value !== isPatientSelected(patient.id)) {
                  togglePatientSelection(patient);
                }
              });
            }}
            aria-label="Select all"
            className="h-5 w-5"
          />
          <span className="text-xs font-medium text-muted-foreground">
            {allSelected ? "Deselect all" : "Select all on page"}
          </span>
        </div>
        {patients.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No patients found.
          </div>
        ) : (
          <ul className="divide-y">
            {patients.map((patient) => {
              const tags = patient.tags || [];
              const callStatus = latestCallStatus(patient);
              const dob = formatDob(patient.date_of_birth);
              return (
                <li
                  key={patient.id}
                  data-state={isPatientSelected(patient.id) ? "selected" : undefined}
                  className="flex items-start gap-3 px-3 py-3 active:bg-muted/50 data-[state=selected]:bg-muted/40"
                  onClick={() => navigate(`/patients/${patient.id}`)}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="pt-0.5 cursor-default"
                  >
                    <Checkbox
                      checked={isPatientSelected(patient.id)}
                      onCheckedChange={() => togglePatientSelection(patient)}
                      aria-label="Select row"
                      className="h-5 w-5"
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 text-[13px] font-medium">
                        <span className="break-words">
                          {patient.first_name} {patient.last_name}
                        </span>
                        {patient.preferred_name && (
                          <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                            ({patient.preferred_name})
                          </span>
                        )}
                      </div>
                      {callStatus && (
                        <span className="shrink-0">
                          <CallStatusIcon status={callStatus} />
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground tabular-nums">
                      {patient.phone_number && (
                        <span className="break-all">{patient.phone_number}</span>
                      )}
                      {dob && <span>DOB {dob}</span>}
                      {patient.external_id && (
                        <span className="break-all" title="MRN / external id">
                          MRN {patient.external_id}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                      {renderConsent(patient)}
                      {tags.slice(0, 3).map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="px-1.5 py-0 text-[11px] font-normal"
                        >
                          {tag}
                        </Badge>
                      ))}
                      {tags.length > 3 && (
                        <span className="text-[11px] text-muted-foreground">
                          +{tags.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Tablet/desktop: table */}
      <div className="hidden md:block flex-1 overflow-auto min-h-0">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={
                    isPatientSelected(row.original.id) ? "selected" : undefined
                  }
                  className="cursor-pointer"
                  onClick={() => navigate(`/patients/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={
                        cell.column.id !== "select" ? "px-4 py-2.5" : "p-0 pl-4"
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-sm text-muted-foreground"
                >
                  No patients found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PatientTable;
